import * as Icons from "@ant-design/icons";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Col, Menu, Row, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DynamicDrawer from "../../components/Settings/DeveloperTools/Tabs/modals/DynamicDrawer";
import { useMenu } from "../../store/context/admin/menu-context";
import { useTenant } from "../../store/context/tenant-context";
import { useTheme } from "../../store/context/theme-context";
import { getSessionData } from "../../utils/interceptors/localStorageService";
// Removed feature gating here; filtering now handled in MenuContext
import "./MainLayout.css";

const { SubMenu } = Menu;

const MainLayout = ({ collapsed, setCollapsed, type }) => {
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedMenuGroup, setSelectedMenuGroup] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerConfig, setDrawerConfig] = useState({
    configCmd: "GetConfigQuery",
    configParams: {
      path: "Webhooks",
      configName: "MenuDrawerConfig",
    },
    width: "50%",
    placement: "right",
  });

  const { theme } = useTheme();
  const tenant = useTenant();

  const location = useLocation();
  const { menuList } = useMenu();
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const storedSessionData = getSessionData();
    if (storedSessionData) {
      setSessionData(storedSessionData);
      // const firstNameInitial = (storedSessionData?.firstName ?? "")
      //   .charAt(0)
      //   .toUpperCase();
      // const lastNameInitial = (storedSessionData?.lastName ?? "")
      //   .charAt(0)
      //   .toUpperCase();
      // setInitials(`${firstNameInitial}${lastNameInitial}`);
    }
  }, []);

  const findMenuGroupByPath = useCallback(
    (currentPath) => {
      if (!menuList?.menus || menuList.menus.length === 0) return null;

      for (const menu of menuList.menus) {
        if (menu.path === currentPath) {
          return menu.menu_groups?.[0] || null;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path === currentPath) {
              return menu.menu_groups?.[0] || null;
            }
          }
        }
      }

      for (const menu of menuList.menus) {
        if (menu.path && currentPath.startsWith(menu.path)) {
          return menu.menu_groups?.[0] || null;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path && currentPath.startsWith(submenu.path)) {
              return menu.menu_groups?.[0] || null;
            }
          }
        }
      }

      return null;
    },
    [menuList]
  );

  const findSelectedCodeByLocation = useCallback(
    (currentPath) => {
      if (!menuList?.menus || menuList.menus.length === 0) return "";

      for (const menu of menuList.menus) {
        if (menu.path === currentPath) {
          return menu.code;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path === currentPath) {
              return submenu.code;
            }
          }
        }
      }

      for (const menu of menuList.menus) {
        if (menu.path && currentPath.startsWith(menu.path)) {
          return menu.code;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path && currentPath.startsWith(submenu.path)) {
              return submenu.code;
            }
          }
        }
      }

      return "";
    },
    [menuList]
  );

  useEffect(() => {
    // Don't fetch menu schema here - AdminLayout handles it
    // This component is only responsible for rendering the menu once it's loaded
    if (!menuList) {
      return;
    }

    const currentPath = location.pathname;
    const menuGroupFromPath = findMenuGroupByPath(currentPath);
    const codeFromLocation = findSelectedCodeByLocation(currentPath);

    // Update selectedCode if we found a valid code
    if (codeFromLocation) {
      setSelectedCode(codeFromLocation);
    }

    // Only derive selectedMenuGroup from URL when not explicitly chosen yet
    if (!selectedMenuGroup) {
      if (menuGroupFromPath) {
        console.log("Initializing menu group from path:", menuGroupFromPath);
        setSelectedMenuGroup(menuGroupFromPath);
      } else if (menuList?.modules?.length > 0) {
        console.log("Setting default menu group:", menuList.modules[0].code);
        setSelectedMenuGroup(menuList.modules[0].code);
      }
    }
    // If a group is already selected (e.g., user clicked a module), do not override it here
  }, [
    menuList,
    location.pathname,
    findMenuGroupByPath,
    findSelectedCodeByLocation,
    selectedMenuGroup,
  ]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const { t } = useTranslation();

  const retrieveMenuData = useCallback(() => {
    // If no menu group is selected, don't show any menus
    if (!selectedMenuGroup) {
      setMenuData([]);
      return;
    }

    var returnData = (menuList?.menus ?? []).filter(
      (item) => item.menu_groups && item.menu_groups.includes(selectedMenuGroup)
    );

    setMenuData(returnData);
  }, [menuList, selectedMenuGroup]);

  useEffect(() => {
    // Only retrieve menu data if we have a valid menu group and menu list
    if (selectedMenuGroup && menuList?.menus) {
      retrieveMenuData();
    }
  }, [selectedMenuGroup, menuList, retrieveMenuData]);

  const navigate = useNavigate();

  // Modified handleMenuItemClick to handle drawer type
  const handleMenuItemClick = (path, code, submenu = null) => {

    // Check if submenu type is drawer
    if (submenu && submenu.type === "drawer") {
      // Set drawer configuration from submenu

      console.log("submenu.formFieldsColumn?? ", submenu.formFieldsColumn);
      setDrawerConfig({
        configCmd: submenu.configCmd || "GetConfigQuery",
        formFieldsColumn: parseInt(submenu.formFieldsColumn) || 2,
        configParams: submenu.configParams || {
          path: submenu.path || "Webhooks",
          configName: submenu.configName || "MenuDrawerConfig",
        },
        width: submenu.width || "50%",
        placement: submenu.placement || "right",
      });
      setDrawerVisible(true);
      return;
    }

    // Normal navigation for non-drawer items
    if (path) {
      navigate(path);
    }
    if (code) {
      setSelectedCode(code);
    }
  };

  const toggleMenuClick = (menuKey) => {
    console.log("menuKey?? ", menuKey, openKeys);
    if (isHovered) {
      setIsHovered(false);
      setOpenKeys([]);
    } else {
      setIsHovered(true);
      setOpenKeys([menuKey]);
    }
  };

  const handleMenuGroupSelect = (group) => {
    console.log("Selecting menu group:", group, "Current:", selectedMenuGroup);
    if (group !== selectedMenuGroup) {
      setSelectedMenuGroup(group);
      if (collapsed) setCollapsed(false);
    }
  };

  // no-op: drawer close handled inline

  // if (isFetchingMenuSchema) return <> </>;

  // Don't render if no menu group is selected or no menu data
  if (!selectedMenuGroup || !menuList?.modules || !menuList?.menus) {
    console.log("Not rendering - missing data:", {
      selectedMenuGroup,
      hasModules: !!menuList?.modules,
      hasMenus: !!menuList?.menus,
    });
    // return <> </>;
  }

  return (
    <>
      <div
        style={{
          overflowY: "auto",
          height: "calc(100vh - 48px)",
          minWidth: 40,
        }}
      >
        <Row className="w-full h-full border-none rounded-none">
          <Col
            span={collapsed ? 24 : 5}
            style={{
              backgroundColor: theme === "dark" ? "#151515" : "#e7e7e7",
            }}
          >
            {collapsed && (
              <div className="py-1.5 pr-4 text-right">
                <Button
                  // type="text"
                  shape="circle"
                  onClick={toggleCollapsed}
                  // style={{
                  //   width: 32,
                  //   height: 32,
                  //   boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  // }}
                  icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
                // className="trigger-button"
                />
              </div>
            )}
            <div className="menu-group-icons">
              {(menuList?.modules ?? []).map((group) => {
                if (group.code.includes("divider")) return <></>;
                const IconComponent = Icons[group.icon];
                const isSelected = selectedMenuGroup === group.code;

                return (
                  <Tooltip
                    key={group.code}
                    title={t(group?.translationCode || group?.name, {
                      defaultValue: group?.name,
                    })}
                    placement="right"
                  >
                    <Button
                      icon={
                        <IconComponent
                          style={{
                            color: isSelected
                              ? tenant.theme.colorPrimary
                              : "inherit",
                          }}
                        />
                      }
                      onClick={() => handleMenuGroupSelect(group.code)}
                      style={{
                        width: "100%",
                        border: "none",
                        fontSize: "14px",
                        borderRadius: 0,
                        borderRight: isSelected
                          ? `2px solid ${tenant.theme.colorPrimary}`
                          : "none",
                        background: isSelected
                          ? `${tenant.theme.colorPrimary}10`
                          : "transparent",
                        boxShadow: "none",
                        padding: "25px 0",
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </Col>
          {!collapsed && (
            <Col
              span={19}
              style={{
                backgroundColor: theme === "dark" ? "#0e0e0e" : "#f3f3f3",
              }}
            >
              <div className="py-1.5 pr-2 text-right">
                <Button
                  shape="circle"
                  onClick={toggleCollapsed}
                  // style={
                  //   {
                  //     // width: 32,
                  //     // height: 32,
                  //     // boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  //   }
                  // }
                  icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
                // className="trigger-button"
                />
              </div>

              <div
                className={`${theme === "dark" ? "text-whitef bg-[#141414]" : "bg-white"
                  } flex items-center justify-start px-1 py-3 pr-3 mb-3 mx-0 gap-3 `}
              >
                {sessionData && (
                  <div
                    className="flex items-center justify-center text-lg text-white rounded-full select-none size-11 min-w-11"
                    style={{ backgroundColor: tenant.theme.colorPrimary }}
                  >
                    {sessionData?.firstName?.charAt(0)}
                    {sessionData?.lastName?.charAt(0)}
                  </div>
                )}
                <div className="flex-1 h-12 text-base">
                  <p>
                    <span className="block text-sm font-medium capitalize">
                      {sessionData?.displayName}
                    </span>
                    <span
                      className={`${theme === "dark" ? "text-[#8c8c8c]" : "text-[#595959]"
                        } text-sm block`}
                    >
                      {sessionData?.userClassificationDescription}
                    </span>
                  </p>
                </div>
              </div>

              {selectedMenuGroup && (
                <Menu
                  style={{ backgroundColor: `${tenant.theme.colorPrimary}10` }}
                  mode="inline"
                >
                  <Menu.Item
                    key={"subItem.label"}
                    icon={(() => {
                      const iconName = (menuList?.modules ?? []).find(
                        (key) => key.code === selectedMenuGroup
                      )?.icon;
                      const IconComponent = Icons[iconName];
                      return IconComponent ? <IconComponent /> : null;
                    })()}
                    style={{
                      borderRight: "none",
                      borderRadius: 0,
                      background: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <div className="block text-sm font-bold capitalize ">
                      {t(
                        (menuList?.modules ?? []).find(
                          (key) => key.code === selectedMenuGroup
                        )?.translationCode ||
                        (menuList?.modules ?? []).find(
                          (key) => key.code === selectedMenuGroup
                        )?.name,
                        {
                          defaultValue: (menuList?.modules ?? []).find(
                            (key) => key.code === selectedMenuGroup
                          )?.name,
                        }
                      )}
                    </div>
                  </Menu.Item>
                </Menu>
              )}

              <Menu
                className="mt-4"
                defaultSelectedKeys={["1"]}
                mode="inline"
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys)}
              >
                {menuData.map((item) => {
                  if (item.submenus) {
                    const visibleSubmenus = item.submenus || [];

                    return (
                      <SubMenu
                        key={item.code}
                        icon={(() => {
                          const IconComponent = Icons[item.icon];
                          return IconComponent ? <IconComponent /> : null;
                        })()}
                        onTitleClick={() => toggleMenuClick(item.code)}
                        title={
                          <div
                            className={
                              openKeys.includes(item.code)
                                ? "block text-sm font-medium capitalize  "
                                : "block text-sm font-normal capitalize  "
                            }
                          >
                            {t(item?.translationCode || item?.label, {
                              defaultValue: item?.label,
                            })}
                          </div>
                        }
                      >
                        {visibleSubmenus.map((subItem) => (
                          // what prop will I use to know if a menu item is active

                          <Menu.Item
                            key={subItem.code}
                            icon={(() => {
                              const IconComponent = Icons[subItem.icon];
                              return IconComponent ? <IconComponent /> : null;
                            })()}
                            onClick={() =>
                              handleMenuItemClick(
                                subItem.path,
                                subItem.code,
                                subItem
                              )
                            }
                            style={{
                              borderRight:
                                selectedCode === subItem.code
                                  ? `4px solid ${tenant.theme.colorPrimary}`
                                  : "none",
                              backgroundColor:
                                selectedCode === subItem.code &&
                                `${tenant.theme.colorPrimary}10`,
                            }}
                            className="!pr-2 !mr-0 !rounded-none"
                          >
                            {t(subItem?.translationCode || subItem?.label, {
                              defaultValue: subItem?.label,
                            })}
                          </Menu.Item>
                        ))}
                      </SubMenu>
                    );
                  }

                  // Top-level single items (without submenus) are assumed pre-filtered in context

                  return (
                    <Menu.Item
                      key={t(item.code)}
                      icon={(() => {
                        const IconComponent = Icons[item.icon];
                        return IconComponent ? <IconComponent /> : null;
                      })()}
                      onClick={() => handleMenuItemClick(item.path)}
                      title={t(item?.translationCode || item?.label, {
                        defaultValue: item?.label,
                      })}
                    >
                      <Tooltip
                        title={t(item?.translationCode || item?.label, {
                          defaultValue: item?.label,
                        })}
                        placement="right"
                      >
                        {t(item?.translationCode || item?.label, {
                          defaultValue: item?.label,
                        })}
                      </Tooltip>
                    </Menu.Item>
                  );
                })}
              </Menu>
            </Col>
          )}
        </Row>
      </div>

      {/* DynamicDrawer Component */}
      <DynamicDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        formFieldsColumn={drawerConfig.formFieldsColumn}
        configCmd={drawerConfig.configCmd}
        configParams={drawerConfig.configParams}
        width={drawerConfig.width}
        placement={drawerConfig.placement}
      />
    </>
  );
};

export default MainLayout;
